import { type FC } from "react";

import { useQuery } from "@apollo/client/react";

import { MeDocument, type RunMetaFragment, RunStatusEnum } from "@app_schema";

import { BasicLink } from "./basic_link";
import { DateTimeFormatter } from "./date_time_formatter";
import { Timer } from "./timer";

const STATUS_TEXT: Record<RunStatusEnum, string> = {
  [RunStatusEnum.Running]: "Running",
  [RunStatusEnum.Paused]: "Paused",
  [RunStatusEnum.Blocked]: "Blocked",
  [RunStatusEnum.Succeeded]: "Succeeded",
  [RunStatusEnum.Failed]: "Failed",
};

// Admin-only one-line run summary shown under a source-file row inside the
// processing card: which workflow processed the file, its status, how long it
// ran, when it finished, and a jump to the run viewer. Surfaces the per-document
// run that the (removed) project-level Workflow Runs panel used to list. Gates
// on role itself so callers can drop it in unconditionally — renders nothing for
// non-admins.
export const RunMeta: FC<{ run: RunMetaFragment }> = ({ run }) => {
  const { data } = useQuery(MeDocument);
  if (data?.me.role !== "admin") return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 pl-3 text-xs text-slate-500">
      <span>{run.workflow.name}</span>
      <span className="text-slate-300">&middot;</span>
      <span>{STATUS_TEXT[run.status]}</span>
      {run.started && run.finished && (
        <>
          <span className="text-slate-300">&middot;</span>
          <Timer from={run.started} till={run.finished} />
        </>
      )}
      {run.finished && (
        <>
          <span className="text-slate-300">&middot;</span>
          <DateTimeFormatter iso={run.finished} format="date" />
        </>
      )}
      <span className="text-slate-300">&middot;</span>
      <BasicLink to={`/dashboard/runs/${run.id}`}>View run</BasicLink>
    </div>
  );
};
